import type {
  EditorElement,
  EditorReactComponent,
  InlineElement,
  RefElement,
} from '@wix/component-protocol/schema';

import { DependenciesContributor, DependencyToken } from '../dependencies';

export const RUNTIME_LABEL_MAPPING_VERSION = 'runtime-labels-v1';
const RUNTIME_LABELS_FETCH_TIMEOUT_MS = 5000;

type RuntimeLabelsFields = Record<string, string>;

type RuntimeLabelsLoadResult = {
  fields: RuntimeLabelsFields;
  cacheable: boolean;
};

export type RuntimeLabelsCompletionInput = {
  componentType: string;
  language: string;
  manifest: EditorReactComponent;
};

export type RuntimeLabelsCompletionResult = {
  manifest: EditorReactComponent;
  cacheable: boolean;
};

export class RuntimeLabelsService {
  #pendingLoads = new Map<string, Promise<RuntimeLabelsLoadResult>>();
  #hasLoggedFetchFailure = false;

  constructor(
    private readonly dependenciesContributor: DependenciesContributor,
    private readonly enabled: boolean,
  ) {}

  async completeLabels({
    componentType,
    language,
    manifest,
  }: RuntimeLabelsCompletionInput): Promise<EditorReactComponent> {
    const result = await this.completeLabelsForCache({
      componentType,
      language,
      manifest,
    });

    return result.manifest;
  }

  async completeLabelsForCache({
    componentType,
    language,
    manifest,
  }: RuntimeLabelsCompletionInput): Promise<RuntimeLabelsCompletionResult> {
    if (!this.enabled) {
      return { manifest, cacheable: true };
    }

    const { fields, cacheable } = await this.#loadFields(
      componentType,
      language,
    );

    return {
      manifest: completeRuntimeManifestLabels(manifest, fields),
      cacheable,
    };
  }

  invalidateCache() {
    this.#pendingLoads.clear();
  }

  async #loadFields(
    componentType: string,
    language: string,
  ): Promise<RuntimeLabelsLoadResult> {
    const cacheKey = getRuntimeLabelsCacheKey(componentType, language);
    const pendingLoad = this.#pendingLoads.get(cacheKey);

    if (pendingLoad) {
      return pendingLoad;
    }

    const load = this.#fetchFields(componentType, language).then((fields) => {
      if (!fields) {
        return { fields: {}, cacheable: false };
      }

      return { fields, cacheable: true };
    });

    this.#pendingLoads.set(cacheKey, load);

    try {
      return await load;
    } finally {
      this.#pendingLoads.delete(cacheKey);
    }
  }

  async #fetchFields(
    componentType: string,
    language: string,
  ): Promise<RuntimeLabelsFields | null> {
    if (
      !this.dependenciesContributor.has(
        DependencyToken.UX_DATA_GET_WITH_DEFAULTS,
      )
    ) {
      return null;
    }

    try {
      const getUxDataWithDefaults = this.dependenciesContributor.get(
        DependencyToken.UX_DATA_GET_WITH_DEFAULTS,
      );
      const fields = await runWithTimeout(
        getUxDataWithDefaults({
          componentType,
          language,
        }),
        RUNTIME_LABELS_FETCH_TIMEOUT_MS,
      );

      return fields;
    } catch (error) {
      this.#warnFetchFailure(componentType, error);
      return null;
    }
  }

  #warnFetchFailure(componentType: string, error: unknown) {
    if (this.#hasLoggedFetchFailure) {
      return;
    }

    this.#hasLoggedFetchFailure = true;
    console.warn(
      `[RuntimeLabelsService] Failed to load runtime labels for ${componentType}. Falling back to stored manifest labels.`,
      error,
    );
  }
}

function runWithTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(`Runtime labels request timed out after ${timeoutMs}ms`),
      );
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    clearTimeout(timeoutId);
  });
}

export function completeRuntimeManifestLabels(
  manifest: EditorReactComponent,
  fields: RuntimeLabelsFields,
): EditorReactComponent {
  const completedManifest = cloneManifest(manifest);
  const translatedDescription = getNonEmptyField(fields, 'description');

  if (translatedDescription !== undefined) {
    completedManifest.description = translatedDescription;
  }

  if (completedManifest.editorElement) {
    completeElementLabels(completedManifest.editorElement, [], fields);
  }

  return completedManifest;
}

function completeElementLabels(
  element: EditorElement | InlineElement,
  elementPath: string[],
  fields: RuntimeLabelsFields,
) {
  const displayName =
    getElementDisplayName(fields, elementPath) ?? element.displayName;

  if (displayName !== undefined) {
    element.displayName = displayName;
  }

  Object.entries(element.elements ?? {}).forEach(([childKey, item]) => {
    const childPath = [...elementPath, childKey];

    if (item?.inlineElement) {
      completeElementLabels(item.inlineElement, childPath, fields);
    }

    if (item?.refElement) {
      completeRefElementLabels(item.refElement, childPath, fields);
    }
  });
}

function completeRefElementLabels(
  refElement: RefElement,
  elementPath: string[],
  fields: RuntimeLabelsFields,
) {
  const displayName =
    getElementDisplayName(fields, elementPath) ??
    refElement.displayName ??
    refElement.resolvedElement?.displayName;

  if (displayName !== undefined) {
    refElement.displayName = displayName;
  }

  if (!refElement.resolvedElement) {
    return;
  }

  if (displayName !== undefined) {
    refElement.resolvedElement.displayName = displayName;
  }

  Object.entries(refElement.resolvedElement.elements ?? {}).forEach(
    ([childKey, item]) => {
      const childPath = [...elementPath, childKey];

      if (item?.inlineElement) {
        completeElementLabels(item.inlineElement, childPath, fields);
      }

      if (item?.refElement) {
        completeRefElementLabels(item.refElement, childPath, fields);
      }
    },
  );
}

function getElementDisplayName(
  fields: RuntimeLabelsFields,
  elementPath: string[],
) {
  return getNonEmptyField(fields, ['.root', ...elementPath].join('.'));
}

function getNonEmptyField(fields: RuntimeLabelsFields, key: string) {
  const value = fields[key];

  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  return undefined;
}

function getRuntimeLabelsCacheKey(componentType: string, language: string) {
  return [componentType, language].join('|');
}

function cloneManifest(manifest: EditorReactComponent): EditorReactComponent {
  return JSON.parse(JSON.stringify(manifest));
}
